import { joinCss, pickCss } from '@/shared/lib/css-utils';
import css from './striped-bar.module.scss';

const styles = pickCss(css, css.root);

type StripedBarProps = {
  background: string;
  className?: string;
  direction?: 'vertical' | 'horizontal';
  /** For vertical: the bar height in px. For horizontal: the fill percentage (0-100). */
  fillValue: number;
  /**
   * For vertical bars only. When provided, the top striped band represents
   * (1 - filledRatio) of the bar height, and the solid fill below represents
   * filledRatio. Value must be 0..1. When omitted, the stripe is a fixed 25%
   * decorative band.
   */
  filledRatio?: number;
  label?: string;
  labelClassName?: string;
  labelColor?: string;
  /** Horizontal only: label shown inside the striped (remaining) portion. */
  stripesLabel?: string;
  stripesLabelColor?: string;
};

export const StripedBar = ({
  background,
  className,
  direction = 'vertical',
  fillValue,
  filledRatio,
  label,
  labelClassName,
  labelColor,
  stripesLabel,
  stripesLabelColor,
}: StripedBarProps) => {
  if (direction === 'horizontal') {
    const pct = Math.min(100, Math.max(0, fillValue));
    return (
      <div className={styles({ direction }, className)} style={{ background }}>
        <div className={css.hFill} style={{ width: `${pct}%` }}>
          {label && (
            <span
              className={joinCss(css.hFillLabel, labelClassName)}
              style={{ color: labelColor }}
            >
              {label}
            </span>
          )}
        </div>
        <div className={css.hStripes}>
          {stripesLabel && (
            <span
              className={css.hStripesLabel}
              style={{ color: stripesLabelColor }}
            >
              {stripesLabel}
            </span>
          )}
        </div>
      </div>
    );
  }

  const clampedRatio =
    filledRatio === undefined
      ? undefined
      : Math.min(1, Math.max(0, filledRatio));
  const stripePct = clampedRatio === undefined ? 25 : (1 - clampedRatio) * 100;

  return (
    <div
      className={styles({ direction }, className)}
      style={{ background, height: fillValue }}
    >
      <div className={css.vStripes} style={{ height: `${stripePct}%` }} />
      <div className={css.vLine} style={{ top: `${stripePct}%` }} />
      {label && (
        <span className={joinCss(css.vLabel, labelClassName)}>{label}</span>
      )}
    </div>
  );
};
